import React, { useEffect, useState } from "react";
import { api, getGps, fmtTime, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, Coffee, LogOut, Play, Loader2, MapPin } from "lucide-react";

export default function TimeClock() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/time/status");
    setStatus(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const action = async (endpoint, label) => {
    setBusy(true);
    try {
      const gps = await getGps({ required: true });
      await api.post(endpoint, { gps });
      toast.success(`${label} recorded`);
      await load();
    } catch (e) {
      const msg = e?.response ? (formatApiError(e.response?.data?.detail) || e.message) : e.message;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  const t = status?.time_entry || {};
  const l = status?.lunch_entry || {};
  const punches = t.punches || [];
  const lastPunch = punches.length ? punches[punches.length - 1] : null;
  // Currently clocked in if either:
  //   - latest punch in the array has no clock_out, OR
  //   - legacy single-segment entry: clock_in set, clock_out empty
  const currentlyIn = lastPunch
    ? !lastPunch.clock_out
    : !!(t.clock_in && !t.clock_out);
  const dayEnded = !currentlyIn && !!t.clock_out;
  const submitted = ["submitted", "approved", "rejected"].includes(status?.status);
  const lunchOpen = !!l.lunch_start && !l.lunch_end;
  const lunchDone = !!l.lunch_end;

  return (
    <div className="space-y-6" data-testid="time-clock-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Time Clock</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Today's Punches</h1>
      </header>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-5 grid grid-cols-2 gap-4">
          <Stat label="First Clock In" value={fmtTime(t.clock_in)} />
          <Stat label={dayEnded ? "Last Clock Out" : (currentlyIn ? "Status" : "Clock Out")} value={dayEnded ? fmtTime(t.clock_out) : (currentlyIn ? "Working ✓" : fmtTime(t.clock_out))} />
          <Stat label="Lunch Start" value={fmtTime(l.lunch_start)} />
          <Stat label="Lunch End" value={fmtTime(l.lunch_end)} />
        </CardContent>
      </Card>

      {punches.length > 1 && (
        <Card className="border-slate-200 rounded-2xl" data-testid="punches-history-card">
          <CardContent className="p-5">
            <h3 className="font-bold font-display mb-2 text-sm">Today's Punches ({punches.length})</h3>
            <div className="space-y-1.5">
              {punches.map((p, idx) => (
                <div key={p.id || idx} className="flex items-center justify-between text-sm border-b border-slate-100 last:border-0 pb-1.5 last:pb-0" data-testid={`punch-row-${idx}`}>
                  <span className="text-slate-500">#{idx + 1}</span>
                  <span className="text-slate-900">{fmtTime(p.clock_in)} <span className="text-slate-400">→</span> {p.clock_out ? fmtTime(p.clock_out) : <span className="text-green-600 font-semibold">in progress</span>}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {dayEnded && !submitted && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="reclock-banner">
          <p className="font-semibold">Clocked out — but you can clock back in.</p>
          <p className="text-xs">Use the Clock In button below to start a new punch. Every segment will be saved and visible to your admin.</p>
        </div>
      )}

      <div className="space-y-3">
        <Button
          disabled={busy || currentlyIn || submitted}
          onClick={() => action("/time/clock-in", "Clock-in")}
          className="w-full h-16 rounded-2xl text-lg font-bold bg-green-600 hover:bg-green-700 btn-press disabled:opacity-50"
          data-testid="clock-in-btn"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Play className="h-6 w-6 mr-2" />}
          {dayEnded ? "Clock Back In" : "Clock In"}
        </Button>

        <Button
          disabled={busy || !currentlyIn || lunchOpen || lunchDone}
          onClick={() => action("/time/lunch-start", "Lunch start")}
          className="w-full h-16 rounded-2xl text-lg font-bold bg-amber-500 hover:bg-amber-600 btn-press disabled:opacity-50 disabled:bg-slate-200 disabled:text-slate-400"
          data-testid="lunch-start-btn"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Coffee className="h-6 w-6 mr-2" />}
          Start Lunch
        </Button>

        <Button
          disabled={busy || !lunchOpen}
          onClick={() => action("/time/lunch-end", "Lunch end")}
          className="w-full h-16 rounded-2xl text-lg font-bold bg-sky-600 hover:bg-sky-700 btn-press disabled:opacity-50 disabled:bg-slate-200 disabled:text-slate-400"
          data-testid="lunch-end-btn"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Coffee className="h-6 w-6 mr-2" />}
          End Lunch
        </Button>

        <Button
          disabled={busy || !currentlyIn}
          onClick={() => action("/time/clock-out", "Clock-out")}
          className="w-full h-16 rounded-2xl text-lg font-bold bg-red-600 hover:bg-red-700 btn-press disabled:opacity-50 disabled:bg-slate-200 disabled:text-slate-400"
          data-testid="clock-out-btn"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <LogOut className="h-6 w-6 mr-2" />}
          Clock Out
        </Button>
      </div>

      <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 flex items-start gap-2 text-xs text-sky-900">
        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <p>GPS is required for every punch. Please keep location services on while clocking in/out and starting/ending lunch.</p>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
      <p className="text-lg font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}
